import { useEffect, useState, useRef, memo, useCallback } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconAdd, IconDelete, IconDrag } from "@icons/index";
import classNames from "classnames";
import { Trans } from "react-i18next";
import { Checkbox, Input, Popconfirm, Popover, Tooltip } from "antd";
import { t } from "i18next";
import { Section } from "@src/ui/Section";
import "./index.scss";
import Empty from "../Empty";
import TruncateTitle from "@components/TruncateTitle";
import CommonButton from "@components/CommonButton";
import { IconLineCirclePlus } from "@kalodata/kds-design-icons";

interface SellingPoint {
  id: string;
  keyWord: string;
  checked: boolean;
}

interface SellingPointProps {
  isOpen?: boolean;
  disabled?: boolean;
  popoverWidth?: number;
  externalData: any;
  onChange?: (data: any) => void;
}

const MAX_SELLING_POINTS = 10;
const COUNTDOWN_DELAY = 5_000;
const COUNTDOWN_DURATION = 120;

const SortableSellingPointItem = ({
  sp,
  index,
  disabled,
  isOpen,
  delModelOpenIndex,
  setDelModelOpenIndex,
  toggleCheck,
  removeSellingPoint,
}: {
  sp: SellingPoint;
  index: number;
  disabled: boolean;
  isOpen?: boolean;
  delModelOpenIndex: string | null;
  setDelModelOpenIndex: (id: string | null) => void;
  toggleCheck: (id: string) => void;
  removeSellingPoint: (id: string) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sp.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : null),
        transition,
        opacity: isDragging ? 0.5 : undefined,
      }}
      className={classNames(
        "relative flex items-center gap-[8px] px-[8px] py-[12px] transition-all bg-kds-bg-card!",
        !disabled && "kalo-tag",
      )}
    >
      {index !== 0 && <div className="absolute top-0 left-[8px] right-[8px] bg-kds-border h-px"></div>}
      {!disabled && <Checkbox checked={sp.checked} onChange={() => toggleCheck(sp.id)} className="mr-2" />}
      <TruncateTitle text={sp.keyWord} lineclamp2 placement="top" className="flex-1" />
      {!disabled && (
        <>
          <Popconfirm
            title={
              <div className="w-full! text-[16px] font-medium px-[12px] py-[14px] border-b border-kds-border">
                <Trans>Are you sure?</Trans>
              </div>
            }
            description={
              <div className="px-[12px] py-[6px] pt-[2px] flex flex-row items-center gap-[8px]">
                <CommonButton
                  className="flex-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDelModelOpenIndex(null);
                  }}
                >
                  <Trans>No</Trans>
                </CommonButton>
                <CommonButton
                  type="primary"
                  className="flex-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSellingPoint(sp.id);
                    setDelModelOpenIndex(null);
                  }}
                >
                  <Trans>Yes</Trans>
                </CommonButton>
              </div>
            }
            showCancel={false}
            open={delModelOpenIndex === sp.id}
            onOpenChange={() => setDelModelOpenIndex(sp.id)}
            placement="bottom"
            icon={null}
            okButtonProps={{ style: { display: "none" } }}
          >
            <div onClick={(e) => e.stopPropagation()} className="flex items-center">
              <IconDelete
                className={classNames(
                  "cursor-pointer selling-point-icon transition-all",
                  isOpen && "selling-point-icon-visible",
                )}
              />
            </div>
          </Popconfirm>

          <div {...attributes} {...listeners} className="flex items-center">
            <IconDrag
              className={classNames(
                "w-[16px] h-[16px] cursor-move shrink-0 selling-point-icon",
                isOpen && "selling-point-icon-visible",
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};

const SellingPoints = memo(({ isOpen, disabled = false, onChange, externalData, popoverWidth }: SellingPointProps) => {
  const [sellingPoints, setSellingPoints] = useState<SellingPoint[]>([]);
  const [newPoint, setNewPoint] = useState("");
  const [delModelOpenIndex, setDelModelOpenIndex] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const idCounterRef = useRef(0);

  useEffect(() => {
    if (externalData.status !== "loading") {
      setCountdown(null);
      return;
    }
    const delayTimer = setTimeout(() => setCountdown(COUNTDOWN_DURATION), COUNTDOWN_DELAY);
    return () => clearTimeout(delayTimer);
  }, [externalData.status]);

  useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    const tick = setTimeout(() => setCountdown((c) => (c !== null ? c - 1 : null)), 1000);
    return () => clearTimeout(tick);
  }, [countdown]);

  useEffect(() => {
    setSellingPoints(
      (externalData?.data || []).map((item: any) => ({
        id: `selling-point-${idCounterRef.current++}`,
        checked: true,
        ...item,
      })),
    );
  }, [JSON.stringify(externalData)]);

  const updateList = useCallback(
    (updater: (prev: SellingPoint[]) => SellingPoint[]) => {
      setSellingPoints((prev) => {
        const next = updater(prev);
        if (next !== prev) onChange?.(next);
        return next;
      });
    },
    [onChange],
  );

  const addSellingPoint = useCallback(
    (keyWord: string) => {
      updateList((prev) => {
        if (prev.length >= MAX_SELLING_POINTS) return prev;
        return [{ id: `selling-point-${idCounterRef.current++}`, keyWord, checked: true }, ...prev];
      });
    },
    [updateList],
  );

  const removeSellingPoint = useCallback(
    (id: string) => updateList((prev) => prev.filter((sp) => sp.id !== id)),
    [updateList],
  );

  const toggleCheck = useCallback(
    (id: string) =>
      updateList((prev) =>
        prev
          .map((sp) => (sp.id === id ? { ...sp, checked: !sp.checked } : sp))
          .sort((a, b) => (b.checked ? 1 : 0) - (a.checked ? 1 : 0)),
      ),
    [updateList],
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      updateList((prev) => {
        const oldIndex = prev.findIndex((sp) => sp.id === active.id);
        const newIndex = prev.findIndex((sp) => sp.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    },
    [updateList],
  );

  const submitNewPoint = useCallback(() => {
    if (!newPoint || newPoint.length >= 100 || disabled) return;
    addSellingPoint(newPoint);
    setNewPoint("");
  }, [newPoint, disabled, addSellingPoint]);

  const isAtLimit = sellingPoints.length >= MAX_SELLING_POINTS;

  return (
    <div className="SellingPoints">
      <div className="relative">
        <Section
          className="bg-kds-bg-card border border-kds-border-subtle rounded-[8px] flex flex-col min-h-[190px] overflow-hidden"
          status={externalData.status || "idle"}
          error={undefined}
        >
          {sellingPoints.length > 0 ? (
            <div className="kds-scroll-thin max-h-[280px] overflow-y-auto flex-1">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sellingPoints.map((sp) => sp.id)} strategy={verticalListSortingStrategy}>
                  <div className="flex flex-col">
                    {sellingPoints.map((sp, index) => (
                      <SortableSellingPointItem
                        key={sp.id}
                        sp={sp}
                        index={index}
                        disabled={disabled}
                        isOpen={isOpen}
                        delModelOpenIndex={delModelOpenIndex}
                        setDelModelOpenIndex={setDelModelOpenIndex}
                        toggleCheck={toggleCheck}
                        removeSellingPoint={removeSellingPoint}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          ) : (
            <Empty
              image="8903ffad.png"
              tips="Add selling points to highlight your product."
              size="small"
              card={false}
            />
          )}
          {!disabled &&
            (!isAtLimit ? (
              <Popover
                content={
                  <div>
                    <div className="border-b border-kds-border p-[12px]">
                      <Input
                        value={newPoint}
                        className="border-kds-border"
                        placeholder={t("Enter your text in any language. We'll auto-translate it automatically.")}
                        onChange={(e) => setNewPoint(e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.code === "Enter" || e.code === "NumpadEnter") && e.keyCode !== 229) submitNewPoint();
                        }}
                      />
                      {newPoint.length >= 100 && (
                        <div className="text-first mt-[4px] text-[14px]">
                          <Trans>String must contain at most 100 character(s)</Trans>
                        </div>
                      )}
                    </div>
                    <div className="p-[12px] pt-[8px]">
                      <CommonButton
                        type="primary"
                        className="whitespace-nowrap h-[26px] text-[14px]"
                        disabled={!newPoint || newPoint.length >= 100}
                        onClick={submitNewPoint}
                      >
                        <Trans>Save</Trans>
                      </CommonButton>
                    </div>
                  </div>
                }
                trigger="click"
                placement="bottom"
                overlayStyle={{ width: popoverWidth || "auto" }}
              >
                <div className="flex items-center justify-center gap-[4px] kalo-text-linear-blue h-[44px] cursor-pointer border-t border-kds-border">
                  <IconLineCirclePlus size={16} />
                  <Trans>Add Selling Point</Trans>
                </div>
              </Popover>
            ) : (
              <Tooltip title={t(`You've reached the limit of ${MAX_SELLING_POINTS} selling points.`)}>
                <div className="flex items-center justify-center gap-[4px] kalo-text-linear-blue h-[44px] border-t border-kds-border cursor-not-allowed opacity-50">
                  <IconAdd className="w-[16px] h-[16px]" />
                  <Trans>Add Selling Point</Trans>
                </div>
              </Tooltip>
            ))}
        </Section>
        {countdown !== null && externalData.status === "loading" && (
          <div className="absolute bottom-[40px] inset-0 flex items-end justify-center">
            <span className="text-[14px] text-kds-fg-subtle">
              <Trans
                i18nKey="video_script_countdown"
                defaults="Good things take time (about {{countDown}}s)"
                values={{ countDown: countdown }}
              />
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

export default SellingPoints;
